import React, { useEffect, useRef, useState } from "react";
import { createAdSlot, isAdsEnabled, AdSlotConfig } from "../../../services/ads";

interface AdSlotProps {
  id: string;
  label?: string;
  description?: string;
  size?: "inline" | "banner" | "interstitial";
  width?: number;
  height?: number;
  format?: "display" | "banner" | "native" | "interstitial";
  position?: "top" | "bottom" | "inline" | "sidebar";
  showPlaceholder?: boolean;
}

const DEFAULT_SIZES = {
  inline: { width: 300, height: 250 }, // Medium Rectangle
  banner: { width: 728, height: 90 }, // Leaderboard
  interstitial: { width: 320, height: 480 } // Mobile Interstitial
};

export const AdSlot: React.FC<AdSlotProps> = ({
  id,
  label,
  description,
  size = "inline",
  width,
  height,
  format = "display",
  position = "inline",
  showPlaceholder = true
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [adLoaded, setAdLoaded] = useState(false);
  const [adError, setAdError] = useState(false);
  const adInitialized = useRef(false);

  const adSize = width && height ? { width, height } : DEFAULT_SIZES[size];

  useEffect(() => {
    if (!isAdsEnabled() || adInitialized.current) return;

    const container = containerRef.current;
    if (!container) return;

    const config: AdSlotConfig = {
      id,
      size: adSize,
      format,
      position
    };

    try {
      createAdSlot(id, config);
      adInitialized.current = true;
      
      // Simulate ad load detection (AdSense doesn't provide direct callbacks)
      const timeout = setTimeout(() => {
        setAdLoaded(true);
      }, 1000);
      
      return () => clearTimeout(timeout);
    } catch (error) {
      console.error(`[AdSlot] Error creating ad slot ${id}:`, error);
      setAdError(true);
    }
  }, [id, adSize.width, adSize.height, format, position]);

  if (!isAdsEnabled() && showPlaceholder) {
    return (
      <section
        aria-label={label || "Advertisement"}
        className={`ad-slot ad-slot--${size} ad-slot--placeholder`}
        data-ad-slot-id={id}
        style={{
          minWidth: adSize.width,
          minHeight: adSize.height,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          background: "rgba(15, 23, 42, 0.4)",
          border: "1px dashed rgba(148, 163, 184, 0.3)",
          borderRadius: 8,
          color: "rgba(148, 163, 184, 0.6)",
          fontSize: "0.75rem",
          padding: "1rem"
        }}
      >
        {label && <div style={{ fontWeight: 600, marginBottom: "0.25rem" }}>{label}</div>}
        {description && <div style={{ fontSize: "0.7rem" }}>{description}</div>}
        <div style={{ marginTop: "0.5rem", fontSize: "0.65rem" }}>Ad Slot ({adSize.width}×{adSize.height})</div>
      </section>
    );
  }

  return (
    <section
      aria-label={label || "Advertisement"}
      className={`ad-slot ad-slot--${size}`}
      data-ad-slot-id={id}
      style={{
        minWidth: adSize.width,
        minHeight: adSize.height,
        display: "flex",
        alignItems: "center",
        justifyContent: "center"
      }}
    >
      <div
        ref={containerRef}
        id={id}
        style={{
          width: "100%",
          minHeight: adSize.height,
          display: "flex",
          alignItems: "center",
          justifyContent: "center"
        }}
      />
      {adError && showPlaceholder && (
        <div
          style={{
            position: "absolute",
            padding: "0.5rem",
            background: "rgba(15, 23, 42, 0.8)",
            borderRadius: 4,
            fontSize: "0.7rem",
            color: "rgba(148, 163, 184, 0.8)"
          }}
        >
          Ad failed to load
        </div>
      )}
    </section>
  );
};



